import { useCallback, useEffect, useRef, useState } from 'react';

import { IdentityLogEvent } from '@localstack/types';

export type IdentityLogPage = {
  items?: IdentityLogEvent[];
  next_cursor?: string | null;
};

export type FetchIdentityEventsFn = (orgId: string | undefined, cursor?: string) => Promise<IdentityLogPage>;

type UseIdentityLogPagesResult = {
  currentPage: IdentityLogPage | undefined;
  pageIndex: number;
  isLoading: boolean;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  goToNextPage: () => void;
  goToPrevPage: () => void;
  refresh: () => void;
};

export const useIdentityLogPages = (
  orgId: string | undefined,
  fetchFn: FetchIdentityEventsFn,
): UseIdentityLogPagesResult => {
  const [pages, setPages] = useState<IdentityLogPage[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [loadingPageIndex, setLoadingPageIndex] = useState<number | null>(null);

  const pagesRef = useRef<IdentityLogPage[]>([]);
  const inFlightRef = useRef<Set<number>>(new Set());
  const generationRef = useRef(0);

  const setPageAtIndex = useCallback((idx: number, value: IdentityLogPage) => {
    setPages((prev) => {
      const next = [...prev];
      next[idx] = value;
      pagesRef.current = next;
      return next;
    });
  }, []);

  const fetchRef = useRef<(index: number, prefetch?: boolean) => Promise<void>>(async () => {});

  const fetchAndPrefetch = useCallback(
    async (index: number, prefetch = true): Promise<void> => {
      if (inFlightRef.current.has(index)) return;

      if (pagesRef.current[index]) {
        const cur = pagesRef.current[index];
        if (prefetch && cur.next_cursor && !pagesRef.current[index + 1]) {
          void fetchRef.current(index + 1, false);
        }
        return;
      }

      const prevPage = pagesRef.current[index - 1];
      const cursor = index === 0 ? undefined : prevPage?.next_cursor;
      if (index > 0 && !cursor) return;

      const generation = generationRef.current;
      inFlightRef.current.add(index);
      setLoadingPageIndex(index);
      try {
        const fetched = await fetchFn(orgId, cursor ?? undefined);
        if (generationRef.current !== generation) return;
        setPageAtIndex(index, fetched);
        if (prefetch && fetched.next_cursor && !pagesRef.current[index + 1]) {
          void fetchRef.current(index + 1, false);
        }
      } finally {
        if (generationRef.current === generation) {
          inFlightRef.current.delete(index);
          setLoadingPageIndex(null);
        }
      }
    },
    [orgId, fetchFn, setPageAtIndex],
  );
  fetchRef.current = fetchAndPrefetch;

  useEffect(() => {
    if (!orgId) {
      return;
    }
    fetchAndPrefetch(pageIndex);
  }, [orgId, pageIndex, fetchAndPrefetch]);

  const goToNextPage = useCallback(() => setPageIndex((i) => i + 1), []);

  const goToPrevPage = useCallback(() => setPageIndex((i) => Math.max(0, i - 1)), []);

  const refresh = useCallback(() => {
    generationRef.current += 1;
    pagesRef.current = [];
    inFlightRef.current = new Set();
    setPages([]);
    fetchAndPrefetch(0);
    setPageIndex(0);
  }, [fetchAndPrefetch]);

  const currentPage = pages[pageIndex];

  return {
    currentPage,
    pageIndex,
    isLoading: pages[pageIndex] === undefined || loadingPageIndex === pageIndex,
    hasPrevPage: pageIndex > 0,
    hasNextPage: !!currentPage?.next_cursor,
    goToNextPage,
    goToPrevPage,
    refresh,
  };
};
